import React from 'react';
import {useSelector} from 'react-redux';
import dateformat from 'dateformat'

import {defaultTheme} from '@ibibo/build-theme';

import * as S from './styles';

import {scrollToEl, detailsNavigationIdsAndValues} from '../../details.config';

import {
  hotelNameSelectedHotelStaticSelector as hnSelectedHotelStaticSelector,
  hotelArea as hotelAreaSelectorFn,
  hotelIdSelector,
  cityId as cityIdSelectorFn,
  bookingWidgetSelector,
  cityNameSelector,
  countryNameSelector
} from '../../selectors';
import {checkinSelectorFn, checkoutSelectorFn, roomStringSelectorFn} from '../../../common/redux/selectors';

import makeSrpUrl from '../../../../utils/makeSrpUrl';

import useCurrentRoomData from '../../../../hooks/useCurrentRoomData';
import useNavigateToReviewPage from '../../../../hooks/useNavigateToReviewPage';

import RupeeIcon from '../../../../../stories/Icons/RupeeIcon';
import TickGreenRound from '../../../../../stories/Icons/Partner/Tick-green';
import TimerIcon from '../../../../../stories/Icons/TimerIcon';

function RoomInfo() {

  const hotelName = useSelector(hnSelectedHotelStaticSelector);
  const hotelArea = useSelector(hotelAreaSelectorFn);
  const hotelCode = useSelector(hotelIdSelector);
  const cityId = useSelector(cityIdSelectorFn);
  const cityName = useSelector(cityNameSelector);
  const countryName = useSelector(countryNameSelector);
  const roomString = useSelector(roomStringSelectorFn);
  const checkinJSDate = useSelector(checkinSelectorFn).date;
  const checkoutJSDate = useSelector(checkoutSelectorFn).date;
  const checkinYYYYMMDD = dateformat(checkinJSDate, 'yyyymmdd');
  const checkoutYYYYMMDD = dateformat(checkoutJSDate, 'yyyymmdd');

  let {
    roomsDataFromHermes = [],
    isHermesLoading,
    hasHermesData,
  } = useSelector(bookingWidgetSelector);

  const isSoldOut = !isHermesLoading && !!hasHermesData && !roomsDataFromHermes.length;

  const [navigator] = useNavigateToReviewPage();

  const currentRoomData = useCurrentRoomData();

  const {name, originalRoomPrice, roomPriceAfterLogin, roomPrice, amenities = [], cltxt, cltxtc, rawData = {}, showDiscount, cbo = []} = currentRoomData;
  const dis = roomPriceAfterLogin - roomPrice;

  const showThisMuchAmenitiesUpfront = (amenities.length > 2 ? 1 : amenities.length);
  const firstDisplayAmenities = amenities.slice(0, showThisMuchAmenitiesUpfront);
  const remainingAmenities = amenities.slice(showThisMuchAmenitiesUpfront);

  const {ibp, rtc, rpc, gtv, gsv, hmd, cot = '', gseg, rtn, pm, fwdp, pc, offercode} = rawData;
  const paxString = rawData.pd?.pax || '';
  const roomPriceInput = rawData.pd?.spr || 0;

  const navigateToReviewPageCaller = () => {
    const queryParamsForReviewUrlProps = {
      hc: hotelCode,
      vert: 'hotels',
      strQry: `hotels-${cityId}-${checkinYYYYMMDD}-${checkoutYYYYMMDD}-${paxString}`,
      v: ibp,
      rtc,
      rpc,
      fwdParams: JSON.stringify(fwdp),
      newui: 'True',
      pah: pm === 2 || pm === 4 ? 'new' : null,
      pay_mode: pm,
      gds: 'true',
      rtn,
      gseg,
      cot,
      hmd,
      gsv,
      hotelArea,
      hotelName,
      cityCode: cityId,
      gtv,
      code: pc || '',
      hOfferCode: offercode,
      rmPrice: roomPriceInput
    };

    const queryParamsForReviewUrlPropsBase64 = {
      data: Buffer.from(JSON.stringify(queryParamsForReviewUrlProps)).toString('base64'),
    };

    const descBlockInput = {
      ...queryParamsForReviewUrlProps,
      paxString,
    };

    navigator(queryParamsForReviewUrlPropsBase64, descBlockInput);
  };

  const showRooms = () => {
    scrollToEl(detailsNavigationIdsAndValues.rooms.id);
  };

  const backToSRP = () => {
    const url = makeSrpUrl({
      checkin: {
        date: new Date(checkinJSDate)
      },
      checkout: {
        date: new Date(checkoutJSDate)
      },
      roomString,
      destination: {
        n: cityName,
        _id: cityId,
        xtr: {
          cc: (countryName.toLowerCase() === 'india' ? 'in' : '')
        }
      },
    });
    window.open(url, '_self');
  }

  // const showSimilar = () => {
  //   scrollToEl(detailsNavigationIdsAndValues.similarHotels.id);
  // };

  if (isSoldOut) {
    return (
      <S.PriceInfoTopBlock>
        <S.SoldOutRoomInfoTopBlock>
          <S.SoldOutWrapperDiv>
            <S.SoldOutInnerDiv>
              <S.IconWrapperSpan>
                <TimerIcon width="5rem" height="5rem" />
              </S.IconWrapperSpan>
            </S.SoldOutInnerDiv>
            <S.SoldOutTextWrappDiv>
              <S.FirstTxtPara>Soldout!</S.FirstTxtPara>
              <span>Your dates are very popular</span>
            </S.SoldOutTextWrappDiv>
          </S.SoldOutWrapperDiv>
        </S.SoldOutRoomInfoTopBlock>
        <S.SoldOutWrapTop>
          <S.BookRoomBtn onClick={backToSRP}>Go Back</S.BookRoomBtn>
        </S.SoldOutWrapTop>
      </S.PriceInfoTopBlock>
    );
  }

  return (
    <S.PriceInfoTopBlock>
      {(cot === 'b2a' && <S.PartnerRateWrap>
        <S.PartnerRateText>Partner Exclusive Rate</S.PartnerRateText>
      </S.PartnerRateWrap>) || null}
      <S.RoomInfoTopBlock>
        <S.RoomTypeInfoTop>
          <S.RoomTypeText>{name}</S.RoomTypeText>
          <S.AmenitesWrapTop>
            {(cltxt && <S.AmenitiesInnerWrapTop>
              <S.TickIconWrapTop>
                <TickGreenRound />
              </S.TickIconWrapTop>
              <S.AmenitiesTextWrapTop>
                <S.AmenitesTopText>{cltxt}</S.AmenitesTopText>
              </S.AmenitiesTextWrapTop>
            </S.AmenitiesInnerWrapTop>) || null}
            {firstDisplayAmenities.map(item => <S.AmenitiesInnerWrapTop>
              <S.TickIconWrapTop>
                <TickGreenRound />
              </S.TickIconWrapTop>
              <S.AmenitiesTextWrapTop>
                <S.AmenitesTopText>{item}</S.AmenitesTopText>
              </S.AmenitiesTextWrapTop>
            </S.AmenitiesInnerWrapTop>)}
            {(remainingAmenities.length && <S.AmenitiesInnerWrapTop onClick={showRooms}>
              <S.AmenitiesTextWrapTop>
                <S.MoreAmenitesText>+ More Amenities</S.MoreAmenitesText>
              </S.AmenitiesTextWrapTop>
            </S.AmenitiesInnerWrapTop>) || null}
          </S.AmenitesWrapTop>
        </S.RoomTypeInfoTop>

        <S.RoomPriceTop>
          {(dis && dis > 0 && showDiscount && roomPriceAfterLogin && <S.OriginalPriceTop>
            <RupeeIcon {...opriceRupeeIconProps} />
            {roomPriceAfterLogin.toLocaleString('en-IN')}
          </S.OriginalPriceTop>)|| null}
          {(roomPrice && <S.NewPriceTop>
            <RupeeIcon {...spriceRupeeIconProps} />
            {roomPrice.toLocaleString('en-IN')}
          </S.NewPriceTop>) || null}
          {(dis && dis > 0 && showDiscount && <S.DealWrapTop>
            <S.DealMainTextTop>(Discount <RupeeIcon {...dpriceRupeeIconProps} />{dis.toLocaleString('en-IN')})</S.DealMainTextTop>
          </S.DealWrapTop>) || null}
          {(cbo && cbo.map(item =>
            <S.YouGetPriceText>Get <RupeeIcon height="0.7rem" width="0.7rem" fill='#fff' />{item.cba} &nbsp;Promotional Discount</S.YouGetPriceText>)
          ) || null}
        </S.RoomPriceTop>

      </S.RoomInfoTopBlock>

      {(hasHermesData ? <S.OtherRoomWrapTop>
        <S.OtherRoomText onClick={showRooms}>Other Rooms</S.OtherRoomText>
        <S.BookRoomBtn onClick={() =>
          navigateToReviewPageCaller()
        }>Book this room</S.BookRoomBtn>
      </S.OtherRoomWrapTop> : <S.OtherRoomWrapTop>
        <S.OtherRoomLoader />
        <S.OtherRoomLoader />
      </S.OtherRoomWrapTop>)}

  </S.PriceInfoTopBlock>
  );
}

export default RoomInfo;

const opriceRupeeIconProps = {
  fill: defaultTheme.colors.grey.base,
  width: defaultTheme.spacing.small.s9,
  height: defaultTheme.spacing.small.s9,
};

const spriceRupeeIconProps = {
  fill: defaultTheme.colors.black.base,
  width: defaultTheme.spacing.large.l13,
  height: defaultTheme.spacing.large.l13,
};

const dpriceRupeeIconProps = {
  fill: defaultTheme.colors.red.base,
  width: defaultTheme.spacing.small.s8,
  height: defaultTheme.spacing.small.s8,
};
